import { useEffect, useMemo, useState } from "react";
import { useTimelineStore } from "../../state";
import { List, TimelineListWrapper } from "./elements";
import { generateTimelineItems } from "./generateTimelineItems";
import { TimelineActionGroup } from "./TimelineActionGroup";
import type { TimelineListProps } from "./types";
import { useScrollHandler } from "./useScrollHandler";

export function TimelineList({
    gaiaPayloadProps,
    logger,
    scrollRef,
    IssueSummaryComponent,
    smartOfferActions,
    isSaleSuccessOrDeclined = false,
}: TimelineListProps) {
    const { markAllMessagesAsRead, getTimelineState, currentSessionId } = useTimelineStore();
    const { messages, autoCompleteSuggestions } = getTimelineState(currentSessionId);
    const { mergedRef, isScrollAtBottom } = useScrollHandler({ scrollRef, messages: messages.length });
    const [shouldShowNewMessageButton, setShouldShowNewMessageButton] = useState(false);

    const timelineItems = useMemo(
        () =>
            generateTimelineItems({
                logger,
                messages,
                scrollRef,
                gaiaPayloadProps,
                smartOfferActions,
                isSaleSuccessOrDeclined,
            }),
        [messages, scrollRef, logger, gaiaPayloadProps, smartOfferActions, isSaleSuccessOrDeclined],
    );

    const unreadMessages = useMemo(() => messages.filter((message) => message.isUnread).length, [messages]);

    useEffect(() => {
        setShouldShowNewMessageButton(unreadMessages > 0 && !isScrollAtBottom);
        if (isScrollAtBottom && unreadMessages > 0) {
            markAllMessagesAsRead(currentSessionId);
        }
    }, [unreadMessages, isScrollAtBottom, markAllMessagesAsRead, currentSessionId]);

    return (
        <>
            <TimelineListWrapper mergedRef={mergedRef}>
                <List data-testid="timeline-list" IssueSummaryComponent={IssueSummaryComponent}>
                    {timelineItems}
                </List>
            </TimelineListWrapper>
            <TimelineActionGroup
                gaiaPayloadProps={gaiaPayloadProps}
                logger={logger}
                scrollRef={scrollRef}
                shouldShowNewMessageButton={shouldShowNewMessageButton}
                unreadMessages={unreadMessages}
                proactiveSuggestions={autoCompleteSuggestions}
            />
        </>
    );
}
